import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
    Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { useCookies } from "react-cookie";
import "dayjs/locale/fi";
import "dayjs/locale/sv";
import LogoutSharpIcon from "@mui/icons-material/LogoutSharp";
import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined"
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined"
import { setLanguage, setSelectedBarcodesSendTo, setSelectedCowListOrder, setShowConfirmationModal, setShowHelpModal, setShowSettingsModal } from "../../store/slices/appSlice";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { FI, SE, GB, US } from 'country-flag-icons/react/3x2'
import { SyntheticEvent, useEffect, useState } from "react";
import HerdSelector from "../components/HerdSelector";
import { fetchAnimalList, fetchBarcodesSendToOptions, fetchCowListOrderOptions, fetchSentBarcodes, saveSettings } from "../../services/homeService";
import { SaveMobiLinkSettingsCommand } from "../../generated/tsClient";
import BarcodeSending from "../components/BarcodeSending";
import IcomoonReact from "icomoon-react";
import iconSet from "../../icons/selection.json";
import ConfirmationModal from "./ConfirmationModal";
import { toast } from "react-toastify";

function SettingsModal(props: any) {
    const { t } = useTranslation(["texts", "errors"]);
    const [cookies, setCookie] = useCookies(["language"]);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const language = useAppSelector((state) => state.app.language);
    const showSettingsModal = useAppSelector((state) => state.app.showSettingsModal);
    const userHerds = useAppSelector((state) => state.app.userHerds);
    const userCanSendToMMfi = useAppSelector((state) => state.app.userCanSendToMMfi);
    const cowListOrderOptions = useAppSelector((state) => state.app.cowListOrderOptions);
    const selectedCowListOrder = useAppSelector((state) => state.app.selectedCowListOrder);
    const barcodesSendToOptions = useAppSelector((state) => state.app.barcodesSendToOptions);
    const selectedBarcodesSendTo = useAppSelector((state) => state.app.selectedBarcodesSendTo);
    const selectedHerd = useAppSelector((state) => state.app.selectedHerd);
    const sentBarcodes = useAppSelector((state) => state.app.sentBarcodes);
    const animalListLoaded = useAppSelector((state) => state.app.animalListLoaded);
    const [languageExpanded, setLanguageExpanded] = useState(false);
    const isAuthorized = useAppSelector((state) => state.app.userIsAuthorized);
    const animalList = useAppSelector((state) => state.app.animalList);

    useEffect(() => {
        if (isAuthorized) {
            fetchCowListOrderOptions(language);
            fetchBarcodesSendToOptions(language);
        }
    }, [language, isAuthorized])

    useEffect(() => {
        if (isAuthorized && selectedHerd != null)
            fetchSentBarcodes(selectedHerd.idkar);
    }, [selectedHerd, isAuthorized])

    const toggleSettingsModal = () => {
        dispatch(setShowSettingsModal(!showSettingsModal));
    };

    const selectLanguage = (language: any) => {
        dispatch(setLanguage(language));
        setCookie("language", language, { path: "/", expires: new Date("9999") });
    };

    const onLogoutClick = () => {
        if(animalList != null && animalList.some(a => a.barcode != null))
            dispatch(setShowConfirmationModal(true));
        else
            onLogout();
    }

    const onLogout = () => {
        dispatch(setShowConfirmationModal(false));
        dispatch(setShowSettingsModal(false));
        navigate("/logout");
    };

    const openHelp = () => {
        dispatch(setShowHelpModal(true));
        dispatch(setShowSettingsModal(false));
    };

    const handleLanguageAccordionChange = () => (event: SyntheticEvent, isExpanded: boolean) => {
        setLanguageExpanded(isExpanded);
    }

    const handleCowListOrderChange = (event: React.MouseEvent<HTMLElement>, newCowListOrder: string | null) => {
        if (newCowListOrder != null)
            dispatch(setSelectedCowListOrder(+newCowListOrder));
    }

    const handleBarcodesSendToChange = (event: React.MouseEvent<HTMLElement>, newBarcodesSendTo: string | null) => {
        if (newBarcodesSendTo != null) {
            const command = new SaveMobiLinkSettingsCommand();
            command.herdId = selectedHerd?.idkar;
            command.sendTo = +newBarcodesSendTo;
            saveSettings(command);
            dispatch(setSelectedBarcodesSendTo(+newBarcodesSendTo));
        }
    }

    const updateAnimalList = () => {
        if (animalList != null && animalList.some(a => a.barcode != null)) {
            toast.error(t("Errors.CantUpdateList"));
        }
        else if(selectedHerd != null)
            fetchAnimalList(selectedHerd.idkar);
    }

    return (
        <Modal isOpen={showSettingsModal} toggle={toggleSettingsModal} size="m" className="settings-modal">
            <ModalHeader toggle={toggleSettingsModal}>
                <Row>
                    <Col><img width="60" src="./img/ProAgria_logo.png" alt="MobiLink" /></Col>
                </Row>
                <Row>
                    <Col>{t("ApplicationName")}</Col>
                </Row>
            </ModalHeader>
            <ModalBody>
                {isAuthorized && selectedHerd != null && (
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Row>
                                <Col xs="1"><IcomoonReact iconSet={iconSet} icon="barcode" size={20} color="#00463E" /></Col>
                                <Col>{t("Menu.SentBarcodes")}</Col>
                            </Row>
                        </AccordionSummary>
                        <AccordionDetails>
                            {
                                sentBarcodes != null && sentBarcodes.length > 0 && sentBarcodes.map((item) => {
                                    return (
                                        <BarcodeSending sending={item} key={item.testMilkDate} />
                                    )
                                })
                            }
                        </AccordionDetails>
                    </Accordion>
                )}
                {isAuthorized && selectedHerd != null && (
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                    >
                        <Row>
                                <Col xs="1"><IcomoonReact iconSet={iconSet} icon="bovine-order" size={20} color="#00463E" /></Col>
                            <Col>{t("Menu.ListOrder")}</Col>
                        </Row>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ToggleButtonGroup
                            value={selectedCowListOrder.toString()}
                            onChange={handleCowListOrderChange}
                            exclusive
                        >
                            {
                                cowListOrderOptions != null &&
                                cowListOrderOptions.map((item) => {
                                    return (
                                        <ToggleButton value={item.paramCode.toString()} key={item.paramCode}>
                                            {item.name}
                                        </ToggleButton>
                                    )
                                })
                            }
                        </ToggleButtonGroup>
                    </AccordionDetails>
                    </Accordion>
                )}
                {isAuthorized && selectedHerd != null && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Row>
                                <Col xs="1"><CachedOutlinedIcon /></Col>
                                <Col>{t("Menu.ListUpdate")}</Col>
                            </Row>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Row><Col>
                                {t("Menu.LastUpdate")} {animalListLoaded ? new Date(animalListLoaded).toLocaleString(t("Locale"), { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : null}
                            </Col></Row>
                            <Row><Col>
                                <Button variant="contained" onClick={updateAnimalList }>{t("Menu.UpdateList")}</Button>
                            </Col></Row>
                        </AccordionDetails>
                    </Accordion>
                )}
                {isAuthorized && selectedHerd != null && userCanSendToMMfi && (
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Row>
                                <Col xs="1"><SwapHorizOutlinedIcon /></Col>
                                <Col>{t("Menu.BarcodesSend")}</Col>
                            </Row>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ToggleButtonGroup
                                value={selectedBarcodesSendTo.toString()}
                                onChange={handleBarcodesSendToChange}
                                exclusive
                            >
                                {
                                    barcodesSendToOptions != null &&
                                    barcodesSendToOptions.map((item) => {
                                        return (
                                            <ToggleButton value={item.paramCode.toString()} key={item.paramCode}>
                                                {item.name}
                                            </ToggleButton>
                                        )
                                    })
                                }
                            </ToggleButtonGroup>
                        </AccordionDetails>
                    </Accordion>
                )}
                {isAuthorized && selectedHerd != null && userHerds != null && userHerds.length > 1 && (
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Row>
                                <Col xs="1"><SearchOutlinedIcon /></Col>
                                <Col>{t("Menu.ChangeHerd")}</Col>
                            </Row>
                        </AccordionSummary>
                        <AccordionDetails>
                            <HerdSelector />
                        </AccordionDetails>
                    </Accordion>
                )}
                <Accordion onChange={handleLanguageAccordionChange()}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                    >
                        {!languageExpanded && language === 'fi' && (<Row><Col xs="1"><FI width="20" /></Col><Col>FI</Col></Row>)}
                        {!languageExpanded && language === 'sv' && (<Row><Col xs="1"><SE width="20" /></Col><Col>SV</Col></Row>)}
                        {!languageExpanded && language === 'en' && (<Row><Col xs="1"><GB width="20" /></Col><Col>EN</Col></Row>)}
                    </AccordionSummary>
                    <AccordionDetails>
                        <Row onClick={(e) => { selectLanguage('fi'); }}><Col xs="1"><FI width="20" /></Col><Col>FI</Col></Row>
                        <Row onClick={(e) => { selectLanguage('sv'); }}><Col xs="1"><SE width="20" /></Col><Col>SV</Col></Row>
                        <Row onClick={(e) => { selectLanguage('en'); }}><Col xs="1"><GB width="20" /></Col><Col>EN</Col></Row>
                    </AccordionDetails>
                </Accordion>
                {isAuthorized && (
                    <Card
                        onClick={openHelp}
                    >
                        <Row>
                            <Col xs="1"><HelpOutlinedIcon role="button" /></Col>
                            <Col>{t("Menu.Help")}</Col>
                        </Row>
                    </Card>
                )}
                <Card
                    onClick={onLogoutClick}
                >
                    <Row>
                        <Col xs="1">
                            <Tooltip title={t("Menu.Logout")}>
                                <LogoutSharpIcon role="button" />
                            </Tooltip>
                        </Col>
                        <Col>
                            {t("Menu.Logout")}
                        </Col>
                    </Row>
                </Card>
            </ModalBody>
            <ConfirmationModal handleOkClick={onLogout} handleCancelClick={() => { dispatch(setShowConfirmationModal(false)) }}
                confirmationText={t("Errors.LogOutBarcodesNotSent")} />
        </Modal>
    );
}

export default SettingsModal;
